// ============================================
// DIGITAL ENGINEERING & MANUFACTURING GUARDRAILS
// COMPLETE PRODUCTION-READY VERSION WITH FULL REPORTING
// ============================================

import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Shield, X, Wrench, CheckCircle, AlertCircle, FileText, Download, Printer } from 'lucide-react';

// ============================================
// 1. CONSTANTS & CONFIGURATION
// ============================================

export const RULE_CATEGORIES = {
  CRITICAL: 'critical',
  WARNING: 'warning',
  INFO: 'info',
  SUGGESTION: 'suggestion'
};

export const PRODUCT_TYPES = {
  MECHANICAL: 'mechanical',
  ELECTRICAL: 'electrical',
  ELECTRONIC: 'electronic',
  SOFTWARE: 'software',
  COMPOSITE: 'composite',
  HYBRID: 'hybrid'
};

export const INDUSTRY_STANDARDS = {
  AEROSPACE: ['AS9100', 'AS9102', 'NADCAP'],
  AUTOMOTIVE: ['IATF 16949', 'ISO 26262', 'APQP'],
  MEDICAL: ['ISO 13485', 'FDA 21 CFR Part 820'],
  DEFENSE: ['ITAR', 'MIL-STD', 'AS9100'],
  GENERAL: ['ISO 9001', 'ISO 14001', 'ISO 45001']
};

// ============================================
// 2. THEME
// ============================================

const T = {
  bg:'#0a0e17', s:'#111827', s2:'#1a2235', s3:'#1e293b', b:'#1e293b',
  t:'#e2e8f0', t2:'#94a3b8', t3:'#64748b', p:'#3b82f6', pl:'#60a5fa',
  g:'#10b981', gl:'#34d399', w:'#f59e0b', wl:'#fbbf24', d:'#ef4444',
  pp:'#8b5cf6', ppl:'#a78bfa', te:'#14b8a6', tel:'#2dd4bf', pk:'#ec4899',
  cy:'#06b6d4', in:'#6366f1', or:'#f97316'
};

const S = {
  card:{ background:T.s2, border:`1px solid ${T.b}`, borderRadius:'7px', padding:'9px', marginBottom:'6px' },
  cHdr:{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'5px', paddingBottom:'4px', borderBottom:`1px solid ${T.b}` },
  cTtl:{ fontSize:'0.82rem', fontWeight:600, display:'flex', alignItems:'center', gap:'4px' },
  badge:(c)=>({ display:'inline-block', padding:'1px 4px', borderRadius:'3px', fontSize:'0.58rem', fontWeight:600, background:`${c}22`, color:c }),
  grid:(n)=>({ display:'grid', gridTemplateColumns:`repeat(${n},1fr)`, gap:'4px' }),
  btn:{ padding:'4px 9px', borderRadius:'4px', border:'none', cursor:'pointer', fontSize:'0.74rem', fontWeight:600, display:'flex', alignItems:'center', gap:'3px', transition:'all 0.2s', whiteSpace:'nowrap' },
  bPri:{ background:`linear-gradient(135deg,${T.p},#2563eb)`, color:'white' },
  bSuc:{ background:`linear-gradient(135deg,${T.g},#059669)`, color:'white' },
  bWar:{ background:`linear-gradient(135deg,${T.w},#d97706)`, color:'white' },
  bDan:{ background:`linear-gradient(135deg,${T.d},#dc2626)`, color:'white' },
  bGho:{ background:'transparent', color:T.t2, border:`1px solid ${T.b}` },
  bPP:{ background:`linear-gradient(135deg,${T.pp},#7c3aed)`, color:'white' },
  bTeal:{ background:`linear-gradient(135deg,${T.te},#0d9488)`, color:'white' },
  statCard:{ background:T.s2, border:`1px solid ${T.b}`, borderRadius:'5px', padding:'6px', textAlign:'center' },
  statVal:{ fontSize:'1.1rem', fontWeight:700, color:T.pl },
  statLbl:{ fontSize:'0.55rem', color:T.t3, textTransform:'uppercase' }
};

// ============================================
// 3. COMPLETE GUARDRAIL ENGINE CLASS
// ============================================

export class DigitalEngineeringGuardrailEngine {
  constructor(config = {}) {
    this.config = {
      strictMode: config.strictMode || false,
      autoFix: config.autoFix || false,
      logLevel: config.logLevel || 'info',
      industry: config.industry || 'General',
      productType: config.productType || 'mechanical',
      ...config
    };
    this.issues = [];
    this.fixes = [];
    this.fixHistory = [];
    this.complianceMetrics = {
      engineering: 0,
      manufacturing: 0,
      quality: 0,
      overall: 0
    };
  }

  // ============================================
  // MAIN VALIDATION ENTRY POINT
  // ============================================
  validate(artifacts) {
    this.issues = [];
    this.fixes = [];
    
    const {
      product = {},
      engineeringSpecs = {},
      ebom = [],
      mbom = [],
      bopData = { functionalGroups: [] },
      equip = [],
      tools = [],
      skills = [],
      qualityPlans = [],
      ewiDocs = []
    } = artifacts;

    const operations = this._extractOperations(bopData);
    const functionalGroups = this._extractFunctionalGroups(bopData);

    this._validateProduct(product);
    this._validateEngineeringSpecs(engineeringSpecs, product);
    this._validateProductEBOMAlignment(product, ebom);
    this._validateEBOMEngineering(ebom);
    this._validateMBOMEngineering(mbom, ebom);
    this._validateBOPEngineering(operations, equip);
    this._validateQualityEngineering(qualityPlans, operations);
    this._validateCrossDomainEngineering(artifacts);
    this._validateManufacturing(artifacts);
    this._calculateComplianceMetrics();

    return this._generateReport();
  }

  // ============================================
  // COMPLETE AUTO-FIX ALL WITH DETAILED REPORT
  // ============================================
  autoFixAll(artifacts) {
    this.fixHistory = [];
    const startTime = Date.now();
    
    const fixedArtifacts = {
      product: { ...(artifacts.product || {}) },
      engineeringSpecs: { ...(artifacts.engineeringSpecs || {}) },
      ebom: Array.isArray(artifacts.ebom) ? [...artifacts.ebom] : [],
      mbom: Array.isArray(artifacts.mbom) ? [...artifacts.mbom] : [],
      equip: Array.isArray(artifacts.equip) ? [...artifacts.equip] : [],
      tools: Array.isArray(artifacts.tools) ? [...artifacts.tools] : [],
      skills: Array.isArray(artifacts.skills) ? [...artifacts.skills] : [],
      qualityPlans: Array.isArray(artifacts.qualityPlans) ? [...artifacts.qualityPlans] : [],
      ewiDocs: Array.isArray(artifacts.ewiDocs) ? [...artifacts.ewiDocs] : [],
      bopData: this._deepCloneBOP(artifacts.bopData)
    };

    let fixesApplied = 0;
    let fixesFailed = 0;
    const fixDetails = [];
    const failedFixes = [];
    const createdArtifacts = [];

    // Track initial state
    const initialState = this._checkAllArtifactsPresent(fixedArtifacts);

    // Create missing artifacts
    const createResults = this._createAllMissingArtifacts(fixedArtifacts);
    fixesApplied += createResults.applied;
    fixesFailed += createResults.failed;
    createdArtifacts.push(...createResults.created);
    fixDetails.push(...createResults.details);

    // Fix all artifacts
    const fixResults = this._fixAllArtifacts(fixedArtifacts);
    fixesApplied += fixResults.applied;
    fixesFailed += fixResults.failed;
    fixDetails.push(...fixResults.details);
    failedFixes.push(...fixResults.failedFixes);

    // Link all artifacts
    const linkResults = this._linkAllArtifacts(fixedArtifacts);
    fixesApplied += linkResults.applied;
    fixesFailed += linkResults.failed;
    fixDetails.push(...linkResults.details);
    failedFixes.push(...linkResults.failedFixes);

    // Re-validate after fixes
    const validationResult = this.validate(fixedArtifacts);
    const finalState = this._checkAllArtifactsPresent(fixedArtifacts);
    const endTime = Date.now();

    // Generate comprehensive report
    const report = this._generateFixReport({
      startTime,
      endTime,
      fixesApplied,
      fixesFailed,
      fixDetails,
      failedFixes,
      createdArtifacts,
      initialState,
      finalState,
      validationResult,
      artifacts: fixedArtifacts
    });

    return {
      artifacts: fixedArtifacts,
      fixesApplied,
      fixesFailed,
      fixHistory: this.fixHistory,
      remainingIssues: this.issues.length,
      allArtifactsPresent: finalState,
      report: report
    };
  }

  // ============================================
  // GENERATE FIX REPORT
  // ============================================
  _generateFixReport(data) {
    const {
      startTime,
      endTime,
      fixesApplied,
      fixesFailed,
      fixDetails,
      failedFixes,
      createdArtifacts,
      initialState,
      finalState,
      validationResult
    } = data;

    const totalTime = (endTime - startTime) / 1000;
    
    // Categorize fixes by domain
    const fixesByDomain = {};
    fixDetails.forEach(fix => {
      if (!fixesByDomain[fix.domain]) fixesByDomain[fix.domain] = [];
      fixesByDomain[fix.domain].push(fix);
    });

    // Categorize failed fixes by reason
    const failuresByReason = {};
    failedFixes.forEach(fix => {
      const reason = fix.reason || 'Unknown';
      if (!failuresByReason[reason]) failuresByReason[reason] = [];
      failuresByReason[reason].push(fix);
    });

    // Calculate success rate
    const totalAttempts = fixesApplied + fixesFailed;
    const successRate = totalAttempts > 0 ? (fixesApplied / totalAttempts * 100) : 100;

    // Determine overall status
    let overallStatus = 'success';
    let statusMessage = 'All issues fixed successfully!';
    if (fixesFailed > 0) {
      overallStatus = 'partial';
      statusMessage = `${fixesApplied} issues fixed, ${fixesFailed} issues could not be fixed`;
    } else if (fixesApplied === 0 && validationResult.issues.length === 0) {
      overallStatus = 'success';
      statusMessage = 'No issues found - system is clean!';
    } else if (fixesApplied === 0 && validationResult.issues.length > 0) {
      overallStatus = 'failed';
      statusMessage = 'No issues could be fixed automatically';
    }

    return {
      summary: {
        overallStatus,
        statusMessage,
        totalTime: totalTime.toFixed(2),
        successRate: successRate.toFixed(1),
        fixesApplied,
        fixesFailed,
        totalAttempts,
        issuesFound: validationResult.issues.length,
        issuesFixed: fixesApplied,
        issuesRemaining: validationResult.issues.length,
        artifactsCreated: createdArtifacts.length
      },
      details: {
        fixesByDomain,
        failedFixes,
        failuresByReason,
        createdArtifacts,
        initialState,
        finalState
      },
      validationReport: validationResult,
      timestamp: new Date().toISOString()
    };
  }

  // ============================================
  // CREATE ALL MISSING ARTIFACTS
  // ============================================
  _createAllMissingArtifacts(artifacts) {
    let applied = 0;
    let failed = 0;
    const created = [];
    const details = [];

    // EBOM
    if (artifacts.ebom.length === 0) {
      const result = this._createEBOM(artifacts);
      applied += result.count;
      created.push({ domain: 'EBOM', count: result.count });
      details.push({ domain: 'EBOM', action: 'created', count: result.count, message: `Created ${result.count} EBOM components` });
      this._logFix('EBOM', 'Product', `Created ${result.count} EBOM components`);
    }

    // MBOM
    if (artifacts.mbom.length === 0) {
      const result = this._createMBOM(artifacts);
      applied += result.count;
      created.push({ domain: 'MBOM', count: result.count });
      details.push({ domain: 'MBOM', action: 'created', count: result.count, message: `Created ${result.count} MBOM components` });
      this._logFix('MBOM', 'Product', `Created ${result.count} MBOM components`);
    }

    // BOP
    const operations = this._extractOperations(artifacts.bopData);
    if (operations.length === 0) {
      const result = this._createBOP(artifacts);
      applied += result.count;
      created.push({ domain: 'BOP', count: result.count });
      details.push({ domain: 'BOP', action: 'created', count: result.count, message: `Created ${result.count} operations` });
      this._logFix('BOP', 'Product', `Created ${result.count} operations`);
    }

    // BOE
    if (artifacts.equip.length === 0) {
      const result = this._createBOE(artifacts);
      applied += result.count;
      created.push({ domain: 'BOE', count: result.count });
      details.push({ domain: 'BOE', action: 'created', count: result.count, message: `Created ${result.count} equipment items` });
      this._logFix('BOE', 'Product', `Created ${result.count} equipment items`);
    }

    // Tools
    if (artifacts.tools.length === 0) {
      const result = this._createTools(artifacts);
      applied += result.count;
      created.push({ domain: 'Tools', count: result.count });
      details.push({ domain: 'Tools', action: 'created', count: result.count, message: `Created ${result.count} tools` });
      this._logFix('Tools', 'Product', `Created ${result.count} tools`);
    }

    // Skills
    if (artifacts.skills.length === 0) {
      const result = this._createSkills(artifacts);
      applied += result.count;
      created.push({ domain: 'Skills', count: result.count });
      details.push({ domain: 'Skills', action: 'created', count: result.count, message: `Created ${result.count} skills` });
      this._logFix('Skills', 'Product', `Created ${result.count} skills`);
    }

    // Quality Plans
    if (artifacts.qualityPlans.length === 0) {
      const result = this._createQualityPlans(artifacts);
      applied += result.count;
      created.push({ domain: 'Quality Plans', count: result.count });
      details.push({ domain: 'Quality Plans', action: 'created', count: result.count, message: `Created ${result.count} quality plans` });
      this._logFix('Quality', 'Product', `Created ${result.count} quality plans`);
    }

    // EWI
    if (artifacts.ewiDocs.length === 0) {
      const result = this._createEWI(artifacts);
      applied += result.count;
      created.push({ domain: 'EWI', count: result.count });
      details.push({ domain: 'EWI', action: 'created', count: result.count, message: `Created ${result.count} EWI documents` });
      this._logFix('EWI', 'Product', `Created ${result.count} EWI documents`);
    }

    return { applied, failed, created, details };
  }

  // ============================================
  // FIX ALL ARTIFACTS
  // ============================================
  _fixAllArtifacts(artifacts) {
    let applied = 0;
    let failed = 0;
    const details = [];
    const failedFixes = [];

    // Product
    const productResult = this._fixProduct(artifacts.product);
    applied += productResult.applied;
    failed += productResult.failed;
    if (productResult.applied > 0) {
      details.push({ domain: 'Product', action: 'fixed', count: productResult.applied, message: `Fixed ${productResult.applied} product fields` });
    }
    if (productResult.failed > 0) {
      failedFixes.push({ domain: 'Product', reason: 'Unable to fix product fields', count: productResult.failed });
    }

    // Engineering Specs
    const specResult = this._fixEngineeringSpecs(artifacts.engineeringSpecs);
    applied += specResult.applied;
    failed += specResult.failed;
    if (specResult.applied > 0) {
      details.push({ domain: 'Engineering Specs', action: 'fixed', count: specResult.applied, message: `Fixed ${specResult.applied} engineering specifications` });
    }

    // EBOM
    const ebomResult = this._fixEBOM(artifacts.ebom);
    applied += ebomResult.applied;
    failed += ebomResult.failed;
    if (ebomResult.applied > 0) {
      details.push({ domain: 'EBOM', action: 'fixed', count: ebomResult.applied, message: `Fixed ${ebomResult.applied} EBOM items` });
    }

    // MBOM
    const mbomResult = this._fixMBOM(artifacts.mbom, artifacts.ebom);
    applied += mbomResult.applied;
    failed += mbomResult.failed;
    if (mbomResult.applied > 0) {
      details.push({ domain: 'MBOM', action: 'fixed', count: mbomResult.applied, message: `Fixed ${mbomResult.applied} MBOM items` });
    }

    // BOP
    const bopResult = this._fixBOP(artifacts.bopData, artifacts.equip);
    applied += bopResult.applied;
    failed += bopResult.failed;
    if (bopResult.applied > 0) {
      details.push({ domain: 'BOP', action: 'fixed', count: bopResult.applied, message: `Fixed ${bopResult.applied} BOP operations` });
    }

    // Equipment
    const equipResult = this._fixEquipment(artifacts.equip);
    applied += equipResult.applied;
    failed += equipResult.failed;
    if (equipResult.applied > 0) {
      details.push({ domain: 'BOE', action: 'fixed', count: equipResult.applied, message: `Fixed ${equipResult.applied} equipment items` });
    }

    // Tools
    const toolsResult = this._fixTools(artifacts.tools);
    applied += toolsResult.applied;
    failed += toolsResult.failed;
    if (toolsResult.applied > 0) {
      details.push({ domain: 'Tools', action: 'fixed', count: toolsResult.applied, message: `Fixed ${toolsResult.applied} tools` });
    }

    // Skills
    const skillsResult = this._fixSkills(artifacts.skills);
    applied += skillsResult.applied;
    failed += skillsResult.failed;
    if (skillsResult.applied > 0) {
      details.push({ domain: 'Skills', action: 'fixed', count: skillsResult.applied, message: `Fixed ${skillsResult.applied} skills` });
    }

    // Quality Plans
    const qualityResult = this._fixQualityPlans(artifacts.qualityPlans);
    applied += qualityResult.applied;
    failed += qualityResult.failed;
    if (qualityResult.applied > 0) {
      details.push({ domain: 'Quality Plans', action: 'fixed', count: qualityResult.applied, message: `Fixed ${qualityResult.applied} quality plans` });
    }

    // EWI
    const ewiResult = this._fixEWI(artifacts.ewiDocs);
    applied += ewiResult.applied;
    failed += ewiResult.failed;
    if (ewiResult.applied > 0) {
      details.push({ domain: 'EWI', action: 'fixed', count: ewiResult.applied, message: `Fixed ${ewiResult.applied} EWI documents` });
    }

    return { applied, failed, details, failedFixes };
  }

  // ============================================
  // CHECK ALL ARTIFACTS PRESENT
  // ============================================
  _checkAllArtifactsPresent(artifacts) {
    const operations = this._extractOperations(artifacts.bopData);
    return {
      product: !!artifacts.product?.name,
      ebom: Array.isArray(artifacts.ebom) && artifacts.ebom.length > 0,
      mbom: Array.isArray(artifacts.mbom) && artifacts.mbom.length > 0,
      bop: operations.length > 0,
      boe: Array.isArray(artifacts.equip) && artifacts.equip.length > 0,
      tools: Array.isArray(artifacts.tools) && artifacts.tools.length > 0,
      skills: Array.isArray(artifacts.skills) && artifacts.skills.length > 0,
      quality: Array.isArray(artifacts.qualityPlans) && artifacts.qualityPlans.length > 0,
      ewi: Array.isArray(artifacts.ewiDocs) && artifacts.ewiDocs.length > 0
    };
  }

  // ============================================
  // CREATE MISSING EBOM
  // ============================================
  _createEBOM(artifacts) {
    const product = artifacts.product || {};
    const name = product.name || 'Product';
    const components = this._generateEBOMRecommendations(product);
    const ebom = [];

    components.slice(0, 3).forEach((comp, i) => {
      ebom.push({
        id: `EBOM-${i + 1}-${Date.now()}`,
        findNumber: `FN-${String(i + 1).padStart(4, '0')}`,
        name: comp,
        description: `${comp} for ${name}`,
        quantity: i === 0 ? 1 : (i + 1) * 2,
        uom: 'EA',
        revision: 'A',
        makeBuy: i === 0 ? 'MAKE' : 'BUY',
        material: i === 0 ? 'Aluminum 6061' : 'Steel 1018',
        weight: (i + 1) * 2.5
      });
    });

    artifacts.ebom = ebom;
    return { count: ebom.length };
  }

  // ============================================
  // CREATE MISSING MBOM
  // ============================================
  _createMBOM(artifacts) {
    const ebom = artifacts.ebom || [];
    const mbom = [];

    ebom.forEach((e, i) => {
      mbom.push({
        id: `MBOM-${i + 1}-${Date.now()}`,
        findNumber: `MFN-${String(i + 1).padStart(4, '0')}`,
        name: `${e.name || 'Component'} - Mfg`,
        description: `Manufacturing version of ${e.name || 'Component'}`,
        quantity: e.quantity || 1,
        uom: e.uom || 'EA',
        revision: 'A',
        makeBuy: e.makeBuy || 'BUY',
        operationReference: String((i + 1) * 10).padStart(4, '0'),
        ebomRef: e.findNumber,
        material: e.material || 'Aluminum 6061',
        weight: e.weight || 2.5
      });
    });

    artifacts.mbom = mbom;
    return { count: mbom.length };
  }

  // ============================================
  // CREATE MISSING BOP
  // ============================================
  _createBOP(artifacts) {
    const product = artifacts.product || {};
    const mbom = artifacts.mbom || [];
    const equipment = artifacts.equip || [];
    const operations = [];
    const wcs = equipment.length > 0 ? equipment.map(e => e.name) : ['WC-CNC-01', 'WC-ASM-01', 'WC-CMM-01'];
    const opTypes = ['Machining', 'Assembly', 'Inspection', 'Surface Treatment', 'Heat Treatment'];

    mbom.forEach((m, i) => {
      if (m.makeBuy === 'MAKE') {
        const opType = opTypes[i % opTypes.length];
        const wc = wcs[i % wcs.length] || 'WC-DEFAULT';
        
        operations.push({
          id: `OP-${i + 1}-${Date.now()}`,
          sequence: String((i + 1) * 10).padStart(4, '0'),
          name: `${opType} - ${m.name || 'Component'}`,
          description: `${opType} operation for ${m.name || 'Component'}`,
          workCenter: wc,
          wc: wc,
          standardTime: 10 + (i * 5),
          stdTime: 10 + (i * 5),
          setupTime: 5 + i,
          setup: 5 + i,
          tools: `Tool-${i + 1}`,
          skills: `Skill-${(i % 3) + 1}`,
          controlKey: `CTRL-${String(i + 1).padStart(4, '0')}`,
          operationType: opType,
          parameter: opType === 'Machining' ? 'Speed: 1000 RPM, Feed: 0.2 mm/rev' : 'Standard parameters',
          consumes: `Material-${i + 1}`,
          output: `Output-${i + 1}`,
          inspection: `Inspection plan for ${m.name || 'Component'}`,
          sourceLocation: 'Warehouse A',
          moveTime: 1.5,
          queTime: 2.5,
          mbomRef: m.findNumber,
          ebomRef: m.ebomRef
        });
      }
    });

    if (operations.length === 0) {
      operations.push({
        id: `OP-1-${Date.now()}`,
        sequence: '0010',
        name: 'Default Machining Operation',
        description: 'Default machining operation',
        workCenter: wcs[0] || 'WC-DEFAULT',
        wc: wcs[0] || 'WC-DEFAULT',
        standardTime: 15,
        stdTime: 15,
        setupTime: 5,
        setup: 5,
        tools: 'Tool-001',
        skills: 'Skill-001',
        controlKey: 'CTRL-0010',
        operationType: 'Machining',
        parameter: 'Speed: 1000 RPM, Feed: 0.2 mm/rev',
        consumes: 'Raw Material',
        output: 'Finished Part',
        inspection: 'Visual + Dimensional'
      });
    }

    artifacts.bopData = {
      productBOP: {
        name: `${product.name || 'Product'}-BOP`,
        description: `BOP for ${product.name || 'Product'}`,
        revision: 'A',
        processes: [{
          name: 'Main Process',
          description: 'Main manufacturing process',
          type: 'ProcessBOP',
          sequence: '0010',
          plantBOPs: [{
            name: 'Main Plant BOP',
            plant: 'JEDDAH',
            description: 'Plant BOP for manufacturing',
            functionalGroups: [{
              name: 'FG-001',
              description: 'Main functional group',
              sequence: '10',
              operations: operations
            }]
          }]
        }]
      }
    };

    return { count: operations.length };
  }

  // ============================================
  // CREATE MISSING BOE
  // ============================================
  _createBOE(artifacts) {
    const equip = [];
    const technologies = ['CNC', 'Conventional', 'Robotics', '3D Printing', 'Casting'];
    const levels = ['WorkUnit', 'ProcessCell', 'ProductionLine'];
    
    technologies.forEach((tech, i) => {
      equip.push({
        id: `EQ-${i + 1}-${Date.now()}`,
        name: `${tech}-${String(i + 1).padStart(3, '0')}`,
        level: levels[i % levels.length],
        description: `${tech} equipment for manufacturing`,
        parent: i > 0 ? `${technologies[0]}-001` : '',
        technology: tech,
        costRate: 100 + (i * 25),
        capacity: 720 - (i * 50),
        bmideType: 'MESWorkUnit',
        precision: 0.01 + (i * 0.005)
      });
    });

    artifacts.equip = equip;
    return { count: equip.length };
  }

  // ============================================
  // CREATE MISSING TOOLS
  // ============================================
  _createTools(artifacts) {
    const tools = [];
    const toolTypes = ['Fixture', 'Cutting Tool', 'Measuring Device', 'Tooling', 'Consumable'];
    const manufacturers = ['Standard Mfg', 'Precision Tools Inc', 'Quality Instruments'];
    
    toolTypes.forEach((type, i) => {
      const date = new Date();
      date.setFullYear(date.getFullYear() + 1);
      tools.push({
        id: `TOOL-${i + 1}-${Date.now()}`,
        name: `${type}-${String(i + 1).padStart(3, '0')}`,
        type: type,
        description: `${type} for manufacturing operations`,
        quantity: 2 + i,
        calibrationDue: date.toISOString().split('T')[0],
        manufacturer: manufacturers[i % manufacturers.length],
        cost: 50 + (i * 30)
      });
    });

    artifacts.tools = tools;
    return { count: tools.length };
  }

  // ============================================
  // CREATE MISSING SKILLS
  // ============================================
  _createSkills(artifacts) {
    const skills = [];
    const skillNames = ['CNC-5AXIS', 'Assembly-LEVEL2', 'Quality-Inspector', 'Welding-Certified', 'Programming'];
    
    skillNames.forEach((name, i) => {
      skills.push({
        id: `SKILL-${i + 1}-${Date.now()}`,
        name: name,
        description: `${name} skill for manufacturing`,
        level: Math.min(i + 1, 5),
        validityMonths: 24,
        certificationBody: 'ISO Standard',
        requiresRecert: i < 3
      });
    });

    artifacts.skills = skills;
    return { count: skills.length };
  }

  // ============================================
  // CREATE MISSING QUALITY PLANS
  // ============================================
  _createQualityPlans(artifacts) {
    const qualityPlans = [];
    const operations = this._extractOperations(artifacts.bopData);
    const targetOps = operations.slice(0, 3).map(op => op.name || 'Default Operation');
    
    if (targetOps.length === 0) {
      targetOps.push('Default Operation');
    }

    targetOps.forEach((op, i) => {
      qualityPlans.push({
        id: `QP-${i + 1}-${Date.now()}`,
        name: `Quality Plan for ${op}`,
        description: `Quality plan for operation: ${op}`,
        targetOperation: op,
        inspectionFrequency: 'Every 100 pieces',
        spcRequirements: i === 0 ? 'SPC Required' : 'Standard',
        characteristics: [
          {
            name: 'Critical Dimension',
            description: 'Verify critical dimensions',
            tolerance: '±0.1mm',
            criticality: 'Critical',
            inspectionMethod: 'CMM',
            datum: 'A'
          },
          {
            name: 'Surface Finish',
            description: 'Check surface quality',
            tolerance: 'Ra 1.6',
            criticality: 'Standard',
            inspectionMethod: 'Visual'
          }
        ]
      });
    });

    artifacts.qualityPlans = qualityPlans;
    return { count: qualityPlans.length };
  }

  // ============================================
  // CREATE MISSING EWI
  // ============================================
  _createEWI(artifacts) {
    const ewiDocs = [];
    const operations = this._extractOperations(artifacts.bopData);
    const targetOps = operations.slice(0, 3).map(op => op.name || 'Default Operation');
    
    if (targetOps.length === 0) {
      targetOps.push('Default Operation');
    }

    const types = ['PDF', 'WORD', 'VIDEO'];
    targetOps.forEach((op, i) => {
      const date = new Date();
      ewiDocs.push({
        id: `EWI-${i + 1}-${Date.now()}`,
        name: `Work Instruction for ${op}`,
        type: types[i % types.length],
        description: `Work instruction for operation: ${op}`,
        targetOperation: op,
        revision: 'A',
        author: 'System Admin',
        approvalDate: date.toISOString().split('T')[0],
        content: `Step-by-step instructions for ${op}`
      });
    });

    artifacts.ewiDocs = ewiDocs;
    return { count: ewiDocs.length };
  }

  // ============================================
  // LINK ALL ARTIFACTS
  // ============================================
  _linkAllArtifacts(artifacts) {
    let applied = 0;
    let failed = 0;
    const details = [];
    const failedFixes = [];

    try {
      const ebomMap = new Map();
      artifacts.ebom.forEach(e => ebomMap.set(e.findNumber, e));

      const mbomMap = new Map();
      artifacts.mbom.forEach(m => {
        mbomMap.set(m.findNumber, m);
        if (m.ebomRef && ebomMap.has(m.ebomRef)) {
          const ebomItem = ebomMap.get(m.ebomRef);
          if (!m.name.includes(ebomItem.name)) {
            m.name = `${ebomItem.name} - Mfg`;
            this._logFix('Linking', m.name, `Linked MBOM to EBOM: ${ebomItem.name}`);
            applied++;
          }
        }
      });

      const operations = this._extractOperations(artifacts.bopData);
      const opMap = new Map();
      operations.forEach(op => {
        opMap.set(op.sequence, op);
        if (op.mbomRef && mbomMap.has(op.mbomRef)) {
          const mbomItem = mbomMap.get(op.mbomRef);
          if (!op.name.includes(mbomItem.name)) {
            this._logFix('Linking', op.name, `Linked operation to MBOM: ${mbomItem.name}`);
            applied++;
          }
        }
      });

      artifacts.qualityPlans.forEach(qp => {
        if (qp.targetOperation && (opMap.has(qp.targetOperation) || operations.some(op => op.name === qp.targetOperation))) {
          this._logFix('Linking', qp.name, `Linked quality plan to operation: ${qp.targetOperation}`);
          applied++;
        }
      });

      artifacts.ewiDocs.forEach(ewi => {
        if (ewi.targetOperation && (opMap.has(ewi.targetOperation) || operations.some(op => op.name === ewi.targetOperation))) {
          this._logFix('Linking', ewi.name, `Linked EWI to operation: ${ewi.targetOperation}`);
          applied++;
        }
      });
    } catch (error) {
      failed++;
      failedFixes.push({ domain: 'Linking', reason: error.message });
    }

    return { applied, failed, details, failedFixes };
  }

  // ============================================
  // GENERATE EBOM RECOMMENDATIONS
  // ============================================
  _generateEBOMRecommendations(product) {
    const recommendations = [];
    const name = product.name?.toLowerCase() || '';
    const type = product.productType || '';
    const desc = product.description?.toLowerCase() || '';

    if (type === PRODUCT_TYPES.MECHANICAL || 
        name.includes('mechanical') || name.includes('structural') ||
        name.includes('housing') || name.includes('bracket')) {
      recommendations.push('Structural Housing/Casing');
      recommendations.push('Fasteners & Hardware');
      recommendations.push('Sealing Components');
    }

    if (name.includes('turbine') || name.includes('aerospace')) {
      recommendations.push('Turbine Blade Assembly');
      recommendations.push('Compressor Housing');
      recommendations.push('Heat Shield');
    }

    if (name.includes('automotive') || name.includes('vehicle')) {
      recommendations.push('Engine Block Assembly');
      recommendations.push('Transmission Assembly');
      recommendations.push('Suspension Components');
    }

    if (recommendations.length === 0) {
      recommendations.push('Main Structural Component');
      recommendations.push('Secondary Support Structure');
      recommendations.push('Fastening System');
    }

    return [...new Set(recommendations)];
  }

  // ============================================
  // EXTRACTION HELPERS
  // ============================================
  _extractOperations(bopData) {
    if (!bopData) return [];
    if (bopData.productBOP) {
      const allOps = [];
      (bopData.productBOP.processes || []).forEach(process => {
        (process.plantBOPs || []).forEach(plantBOP => {
          (plantBOP.functionalGroups || []).forEach(fg => {
            (fg.operations || []).forEach(op => {
              allOps.push({
                ...op,
                functionalGroup: fg.name,
                plant: plantBOP.plant,
                process: process.name,
                processSequence: process.sequence
              });
            });
          });
        });
      });
      return allOps;
    }
    return (bopData.functionalGroups || []).flatMap(fg => 
      (fg.operations || []).map(op => ({
        ...op,
        functionalGroup: fg.name,
        plant: fg.plant || 'N/A'
      }))
    );
  }

  _extractFunctionalGroups(bopData) {
    if (!bopData) return [];
    if (bopData.productBOP) {
      const groups = [];
      (bopData.productBOP.processes || []).forEach(process => {
        (process.plantBOPs || []).forEach(plantBOP => {
          (plantBOP.functionalGroups || []).forEach(fg => {
            groups.push({
              ...fg,
              plant: plantBOP.plant,
              process: process.name,
              plantBOPName: plantBOP.name
            });
          });
        });
      });
      return groups;
    }
    return bopData.functionalGroups || [];
  }

  _deepCloneBOP(bopData) {
    if (!bopData) return { functionalGroups: [] };
    return JSON.parse(JSON.stringify(bopData));
  }

  // ============================================
  // VALIDATION METHODS
  // ============================================
  _validateProduct(product) {
    const required = ['name', 'description', 'productType', 'industry', 'revision'];
    required.forEach(field => {
      if (!product[field]) {
        this._addIssue({
          level: RULE_CATEGORIES.CRITICAL,
          domain: 'Product Definition',
          item: product.name || 'Unnamed Product',
          message: `Missing required product field: ${field}`,
          fix: `Add ${field} to product definition`,
          field: field
        });
      }
    });

    if (product.productType && !Object.values(PRODUCT_TYPES).includes(product.productType)) {
      this._addIssue({
        level: RULE_CATEGORIES.WARNING,
        domain: 'Product Definition',
        item: product.name || 'Unnamed Product',
        message: `Invalid product type: ${product.productType}`,
        fix: `Set product type to one of: ${Object.values(PRODUCT_TYPES).join(', ')}`
      });
    }

    if (product.industry) {
      const standards = INDUSTRY_STANDARDS[product.industry.toUpperCase()] || INDUSTRY_STANDARDS.GENERAL;
      this._addIssue({
        level: RULE_CATEGORIES.INFO,
        domain: 'Product Definition',
        item: product.name || 'Unnamed Product',
        message: `Recommended industry standards: ${standards.join(', ')}`,
        fix: `Ensure compliance with ${standards[0]} for ${product.industry} industry`
      });
    }
  }

  _validateEngineeringSpecs(specs, product) {
    const required = ['dimensions', 'material', 'toleranceClass'];
    required.forEach(field => {
      if (!specs[field]) {
        this._addIssue({
          level: RULE_CATEGORIES.CRITICAL,
          domain: 'Engineering Specs',
          item: product.name || 'Unnamed Product',
          message: `Missing engineering specification: ${field}`,
          fix: `Define ${field} for product "${product.name}"`,
          field: field
        });
      }
    });

    if (specs.material && specs.manufacturingProcess) {
      const material = specs.material.toLowerCase();
      const process = specs.manufacturingProcess.toLowerCase();
      const compatible = {
        'aluminum': ['casting', 'machining', 'extrusion', 'forging'],
        'steel': ['forging', 'machining', 'casting', 'welding'],
        'titanium': ['machining', 'casting', '3d printing'],
        'plastic': ['injection molding', '3d printing', 'extrusion'],
        'composite': ['layup', 'compression molding', 'autoclave']
      };
      
      Object.entries(compatible).forEach(([mat, processes]) => {
        if (material.includes(mat) && !processes.some(p => process.includes(p))) {
          this._addIssue({
            level: RULE_CATEGORIES.WARNING,
            domain: 'Engineering Specs',
            item: product.name || 'Unnamed Product',
            message: `Material "${material}" may not be compatible with process "${process}"`,
            fix: `Consider using compatible processes: ${processes.join(', ')}`
          });
        }
      });
    }
  }

  _validateProductEBOMAlignment(product, ebom) {
    if (!Array.isArray(ebom) || ebom.length === 0) {
      this._addIssue({
        level: RULE_CATEGORIES.CRITICAL,
        domain: 'Product-EBOM Alignment',
        item: product.name || 'Unnamed Product',
        message: `No EBOM exists for product "${product.name}"`,
        fix: 'Create EBOM structure for this product'
      });
      return;
    }

    const recommendations = this._generateEBOMRecommendations(product);
    if (recommendations.length > 0) {
      recommendations.slice(0, 3).forEach(rec => {
        this._addIssue({
          level: RULE_CATEGORIES.SUGGESTION,
          domain: 'Product-EBOM Alignment',
          item: product.name,
          message: `Recommended EBOM component: ${rec}`,
          fix: `Consider adding ${rec} to EBOM for complete product definition`
        });
      });
    }
  }

  _validateEBOMEngineering(ebom) {
    ebom.forEach(item => {
      if (item.makeBuy === 'MAKE' && !item.material) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'EBOM-Engineering',
          item: item.name,
          message: `MAKE item ${item.name} missing material specification`,
          fix: 'Add material specification for manufacturing'
        });
      }

      if (item.quantity && item.quantity < 0) {
        this._addIssue({
          level: RULE_CATEGORIES.CRITICAL,
          domain: 'EBOM-Engineering',
          item: item.name,
          message: `Invalid quantity: ${item.quantity}`,
          fix: 'Set positive quantity'
        });
      }
    });
  }

  _validateMBOMEngineering(mbom, ebom) {
    const ebomFindNumbers = new Set(ebom.map(e => e.findNumber));
    
    mbom.forEach(item => {
      if (item.ebomRef && !ebomFindNumbers.has(item.ebomRef)) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'MBOM-Engineering',
          item: item.name,
          message: `MBOM item ${item.name} references non-existent EBOM ${item.ebomRef}`,
          fix: 'Verify EBOM reference or remove it'
        });
      }

      if (item.makeBuy === 'MAKE' && !item.operationReference) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'MBOM-Engineering',
          item: item.name,
          message: `MAKE item ${item.name} missing operation reference`,
          fix: 'Add operation reference to link to BOP'
        });
      }
    });
  }

  _validateBOPEngineering(operations, equipment) {
    const equipMap = new Map(equipment.map(e => [e.name, e]));

    operations.forEach(op => {
      if (!op.sequence || !op.name || !op.workCenter) {
        this._addIssue({
          level: RULE_CATEGORIES.CRITICAL,
          domain: 'BOP-Engineering',
          item: op.name || 'Unnamed',
          message: `Missing required BOP field: ${!op.sequence ? 'sequence' : !op.name ? 'name' : 'workCenter'}`,
          fix: 'Add missing required field'
        });
      }

      const wc = op.workCenter || op.wc;
      if (wc && !equipMap.has(wc)) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'BOP-Engineering',
          item: op.name,
          message: `Work center ${wc} not found in equipment list`,
          fix: 'Add work center to BOE or correct work center name'
        });
      }

      if (op.operationType === 'Machining' && !op.parameter) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'BOP-Engineering',
          item: op.name,
          message: `Machining operation ${op.name} missing engineering parameters`,
          fix: 'Add cutting parameters (speed, feed, depth of cut)'
        });
      }
    });
  }

  _validateQualityEngineering(qualityPlans, operations) {
    const opMap = new Map(operations.map(op => [op.name, op]));

    qualityPlans.forEach(qp => {
      if (qp.targetOperation && !opMap.has(qp.targetOperation)) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'Quality-Engineering',
          item: qp.name,
          message: `Quality plan ${qp.name} targets non-existent operation: ${qp.targetOperation}`,
          fix: 'Update target operation or create it'
        });
      }

      if (qp.characteristics && qp.characteristics.length > 0) {
        const hasCritical = qp.characteristics.some(c => 
          c.criticality === 'Critical' || c.criticality === 'Key'
        );
        if (!hasCritical) {
          this._addIssue({
            level: RULE_CATEGORIES.INFO,
            domain: 'Quality-Engineering',
            item: qp.name,
            message: `Quality plan ${qp.name} has no critical characteristics defined`,
            fix: 'Identify critical characteristics for this operation'
          });
        }
      }
    });

    const coveredOps = new Set(qualityPlans.map(qp => qp.targetOperation).filter(Boolean));
    operations.forEach(op => {
      if (!coveredOps.has(op.name) && !coveredOps.has(op.sequence)) {
        this._addIssue({
          level: RULE_CATEGORIES.INFO,
          domain: 'Quality-Engineering',
          item: op.name,
          message: `Operation ${op.name} has no quality plan`,
          fix: 'Create quality plan for this operation'
        });
      }
    });
  }

  _validateCrossDomainEngineering(artifacts) {
    const { product, ebom, mbom, bopData, equip, qualityPlans, ewiDocs } = artifacts;
    const operations = this._extractOperations(bopData);

    const hasEBOM = Array.isArray(ebom) && ebom.length > 0;
    const hasMBOM = Array.isArray(mbom) && mbom.length > 0;
    const hasBOP = operations.length > 0;
    const hasBOE = Array.isArray(equip) && equip.length > 0;
    const hasQuality = Array.isArray(qualityPlans) && qualityPlans.length > 0;
    const hasEWI = Array.isArray(ewiDocs) && ewiDocs.length > 0;

    const missing = [];
    if (!hasEBOM) missing.push('EBOM');
    if (!hasMBOM) missing.push('MBOM');
    if (!hasBOP) missing.push('BOP');
    if (!hasBOE) missing.push('BOE');
    if (!hasQuality) missing.push('Quality Plans');
    if (!hasEWI) missing.push('EWI');

    if (missing.length > 0) {
      this._addIssue({
        level: RULE_CATEGORIES.CRITICAL,
        domain: 'Digital Thread',
        item: product.name || 'Product',
        message: `Incomplete digital thread - missing: ${missing.join(', ')}`,
        fix: `Create missing artifacts: ${missing.join(', ')} for complete traceability`
      });
    }
  }

  _validateManufacturing(artifacts) {
    const { equip, tools, skills } = artifacts;
    const operations = this._extractOperations(artifacts.bopData);

    const wcDemand = {};
    operations.forEach(op => {
      const wc = op.workCenter || op.wc;
      if (wc) {
        wcDemand[wc] = (wcDemand[wc] || 0) + (op.standardTime || op.stdTime || 0);
      }
    });

    equip.forEach(eq => {
      if (eq.level === 'WorkUnit' && eq.capacity) {
        const demand = wcDemand[eq.name] || 0;
        if (demand > eq.capacity * 0.9) {
          this._addIssue({
            level: RULE_CATEGORIES.WARNING,
            domain: 'Manufacturing',
            item: eq.name,
            message: `Work center ${eq.name} at ${(demand/eq.capacity*100).toFixed(0)}% capacity`,
            fix: 'Add capacity or redistribute work'
          });
        }
      }
    });

    tools.forEach(tool => {
      if (tool.calibrationDue) {
        const dueDate = new Date(tool.calibrationDue);
        const now = new Date();
        const daysUntil = (dueDate - now) / (1000 * 60 * 60 * 24);
        if (daysUntil < 0) {
          this._addIssue({
            level: RULE_CATEGORIES.CRITICAL,
            domain: 'Manufacturing',
            item: tool.name,
            message: `Tool ${tool.name} calibration expired`,
            fix: 'Recalibrate tool'
          });
        }
      }
    });

    const requiredSkills = new Set();
    operations.forEach(op => {
      if (op.skills) {
        const skillList = Array.isArray(op.skills) ? op.skills : op.skills.split(',').map(s => s.trim());
        skillList.forEach(s => requiredSkills.add(s));
      }
    });
    const availableSkills = new Set(skills.map(s => s.name));
    requiredSkills.forEach(required => {
      if (!availableSkills.has(required)) {
        this._addIssue({
          level: RULE_CATEGORIES.WARNING,
          domain: 'Manufacturing',
          item: required,
          message: `Required skill "${required}" not found in skills inventory`,
          fix: 'Add skill or train personnel'
        });
      }
    });
  }

  // ============================================
  // COMPLIANCE METRICS
  // ============================================
  _calculateComplianceMetrics() {
    const engineeringIssues = this.issues.filter(i => 
      i.domain?.includes('Engineering') || 
      i.domain?.includes('Product') || 
      i.domain?.includes('Specs')
    ).length;
    this.complianceMetrics.engineering = Math.max(0, 100 - (engineeringIssues * 5));

    const manufacturingIssues = this.issues.filter(i => 
      i.domain === 'Manufacturing' || i.domain === 'BOP-Engineering'
    ).length;
    this.complianceMetrics.manufacturing = Math.max(0, 100 - (manufacturingIssues * 3));

    const qualityIssues = this.issues.filter(i => 
      i.domain === 'Quality-Engineering' || i.domain === 'Traceability'
    ).length;
    this.complianceMetrics.quality = Math.max(0, 100 - (qualityIssues * 4));

    this.complianceMetrics.overall = (this.complianceMetrics.engineering * 0.4) +
                                     (this.complianceMetrics.manufacturing * 0.35) +
                                     (this.complianceMetrics.quality * 0.25);
  }

  // ============================================
  // ISSUE MANAGEMENT
  // ============================================
  _addIssue(issue) {
    const existing = this.issues.find(i => 
      i.message === issue.message && 
      i.domain === issue.domain && 
      i.item === issue.item
    );
    if (!existing) {
      this.issues.push(issue);
    }
  }

  // ============================================
  // REPORT GENERATION
  // ============================================
  _generateReport() {
    const critical = this.issues.filter(i => i.level === RULE_CATEGORIES.CRITICAL);
    const warnings = this.issues.filter(i => i.level === RULE_CATEGORIES.WARNING);
    const info = this.issues.filter(i => i.level === RULE_CATEGORIES.INFO);
    const suggestions = this.issues.filter(i => i.level === RULE_CATEGORIES.SUGGESTION);

    const summary = {
      total: this.issues.length,
      critical: critical.length,
      warnings: warnings.length,
      info: info.length,
      suggestions: suggestions.length,
      valid: this.issues.length === 0
    };

    const byDomain = {};
    this.issues.forEach(issue => {
      if (!byDomain[issue.domain]) byDomain[issue.domain] = [];
      byDomain[issue.domain].push(issue);
    });

    return {
      summary,
      issues: this.issues,
      critical,
      warnings,
      info,
      suggestions,
      byDomain,
      complianceMetrics: this.complianceMetrics,
      timestamp: new Date().toISOString(),
      config: this.config
    };
  }

  // ============================================
  // PRODUCT READINESS SCORE
  // ============================================
  getProductReadinessScore() {
    const weights = {
      productDefinition: 15,
      engineeringSpecs: 15,
      ebomCompleteness: 15,
      mbomCompleteness: 15,
      bopCompleteness: 15,
      qualityCompleteness: 15,
      traceability: 10
    };

    let score = 0;
    const issues = this.issues;
    
    const productIssues = issues.filter(i => i.domain === 'Product Definition' || i.domain === 'Engineering Specs');
    score += weights.productDefinition * (1 - Math.min(1, productIssues.length / 5));
    
    const ebomIssues = issues.filter(i => i.domain === 'EBOM-Engineering' || i.domain === 'Product-EBOM Alignment');
    score += weights.ebomCompleteness * (1 - Math.min(1, ebomIssues.length / 5));
    
    const mbomIssues = issues.filter(i => i.domain === 'MBOM-Engineering');
    score += weights.mbomCompleteness * (1 - Math.min(1, mbomIssues.length / 5));
    
    const bopIssues = issues.filter(i => i.domain === 'BOP-Engineering');
    score += weights.bopCompleteness * (1 - Math.min(1, bopIssues.length / 5));
    
    const qualityIssues = issues.filter(i => i.domain === 'Quality-Engineering');
    score += weights.qualityCompleteness * (1 - Math.min(1, qualityIssues.length / 5));
    
    const traceIssues = issues.filter(i => i.domain === 'Traceability' || i.domain === 'Digital Thread');
    score += weights.traceability * (1 - Math.min(1, traceIssues.length / 3));

    return Math.max(0, Math.min(100, score));
  }

  // ============================================
  // RECOMMENDATIONS
  // ============================================
  getRecommendations() {
    const recommendations = [];
    const criticalIssues = this.issues.filter(i => i.level === RULE_CATEGORIES.CRITICAL);
    const warnings = this.issues.filter(i => i.level === RULE_CATEGORIES.WARNING);

    if (criticalIssues.length > 0) {
      recommendations.push({
        priority: 'High',
        category: 'Critical Fixes',
        items: criticalIssues.map(i => ({
          domain: i.domain,
          issue: i.message,
          fix: i.fix
        }))
      });
    }

    if (warnings.length > 0) {
      recommendations.push({
        priority: 'Medium',
        category: 'Warnings',
        items: warnings.map(i => ({
          domain: i.domain,
          issue: i.message,
          fix: i.fix
        }))
      });
    }

    const productType = this.config.productType || 'mechanical';
    const industry = this.config.industry || 'General';

    recommendations.push({
      priority: 'Low',
      category: 'Best Practices',
      items: [
        {
          domain: 'Engineering',
          issue: `Consider ${industry} industry best practices for ${productType} products`,
          fix: `Review ${industry} standards for ${productType} product development`
        },
        {
          domain: 'Quality',
          issue: 'Implement automated quality checks where possible',
          fix: 'Define automated inspection points in quality plans'
        },
        {
          domain: 'Traceability',
          issue: 'Ensure complete digital thread from concept to production',
          fix: 'Verify all artifacts are linked and traceable'
        }
      ]
    });

    return recommendations;
  }

  // ============================================
  // PRODUCT FIXES
  // ============================================
  _fixProduct(product) {
    let applied = 0;
    let failed = 0;

    try {
      if (!product.name || product.name.length < 3) {
        product.name = product.name || 'Unnamed-Product';
        this._logFix('Product', 'name', 'Added default product name');
        applied++;
      }

      if (!product.description || product.description.length < 10) {
        product.description = product.description || `${product.name || 'Product'} - Engineering Specification`;
        this._logFix('Product', 'description', 'Added default description');
        applied++;
      }

      if (!product.productType || !Object.values(PRODUCT_TYPES).includes(product.productType)) {
        product.productType = PRODUCT_TYPES.MECHANICAL;
        this._logFix('Product', 'productType', `Set to ${PRODUCT_TYPES.MECHANICAL}`);
        applied++;
      }

      if (!product.industry) {
        product.industry = 'General';
        this._logFix('Product', 'industry', 'Set to General');
        applied++;
      }

      if (!product.revision || !/^[A-Z][0-9]*(?:\.[0-9]+)?$/.test(product.revision)) {
        product.revision = 'A';
        this._logFix('Product', 'revision', 'Set to A');
        applied++;
      }
    } catch (error) {
      failed++;
    }

    return { applied, failed };
  }

  // ============================================
  // ENGINEERING SPECS FIXES
  // ============================================
  _fixEngineeringSpecs(specs) {
    let applied = 0;
    let failed = 0;

    try {
      if (!specs.dimensions || typeof specs.dimensions !== 'object') {
        specs.dimensions = { length: 0, width: 0, height: 0, weight: 0 };
        this._logFix('Engineering Specs', 'dimensions', 'Added default dimensions');
        applied++;
      }

      if (!specs.material) {
        specs.material = 'Aluminum 6061';
        this._logFix('Engineering Specs', 'material', 'Set to Aluminum 6061');
        applied++;
      }

      if (!specs.toleranceClass || !['Fine', 'Medium', 'Coarse', 'Custom'].includes(specs.toleranceClass)) {
        specs.toleranceClass = 'Medium';
        this._logFix('Engineering Specs', 'toleranceClass', 'Set to Medium');
        applied++;
      }

      if (!specs.manufacturingProcess) {
        specs.manufacturingProcess = 'Machining';
        this._logFix('Engineering Specs', 'manufacturingProcess', 'Set to Machining');
        applied++;
      }
    } catch (error) {
      failed++;
    }

    return { applied, failed };
  }

  // ============================================
  // EBOM FIXES
  // ============================================
  _fixEBOM(ebom) {
    let applied = 0;
    let failed = 0;

    ebom.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `EBOM-${index + 1}-${Date.now()}`;
          this._logFix('EBOM', item.name || `Item-${index}`, 'Added ID');
          applied++;
        }

        if (!item.findNumber || !/^[A-Z0-9-]+$/.test(item.findNumber)) {
          item.findNumber = `FN-${String(index + 1).padStart(4, '0')}`;
          this._logFix('EBOM', item.name || `Item-${index}`, `Set findNumber to ${item.findNumber}`);
          applied++;
        }

        if (!item.name) {
          item.name = `Component-${String(index + 1).padStart(3, '0')}`;
          this._logFix('EBOM', `Item-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.quantity || item.quantity <= 0) {
          item.quantity = 1;
          this._logFix('EBOM', item.name, 'Set quantity to 1');
          applied++;
        }

        if (!item.uom || !['EA', 'KIT', 'SET', 'KG', 'L', 'M', 'CM', 'MM'].includes(item.uom)) {
          item.uom = 'EA';
          this._logFix('EBOM', item.name, 'Set UOM to EA');
          applied++;
        }

        if (!item.makeBuy || !['MAKE', 'BUY', 'RAW', 'CONSUMABLE'].includes(item.makeBuy)) {
          item.makeBuy = 'BUY';
          this._logFix('EBOM', item.name, 'Set makeBuy to BUY');
          applied++;
        }

        if (item.makeBuy === 'MAKE' && !item.material) {
          item.material = 'Aluminum 6061';
          this._logFix('EBOM', item.name, 'Added default material for MAKE item');
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - Engineering Component`;
          this._logFix('EBOM', item.name, 'Added default description');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // MBOM FIXES
  // ============================================
  _fixMBOM(mbom, ebom) {
    let applied = 0;
    let failed = 0;
    const ebomMap = new Map(ebom.map(e => [e.findNumber, e]));

    mbom.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `MBOM-${index + 1}-${Date.now()}`;
          this._logFix('MBOM', item.name || `Item-${index}`, 'Added ID');
          applied++;
        }

        if (!item.findNumber || !/^[A-Z0-9-]+$/.test(item.findNumber)) {
          item.findNumber = `MFN-${String(index + 1).padStart(4, '0')}`;
          this._logFix('MBOM', item.name || `Item-${index}`, `Set findNumber to ${item.findNumber}`);
          applied++;
        }

        if (!item.name) {
          item.name = `Mfg-Component-${String(index + 1).padStart(3, '0')}`;
          this._logFix('MBOM', `Item-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.quantity || item.quantity <= 0) {
          item.quantity = 1;
          this._logFix('MBOM', item.name, 'Set quantity to 1');
          applied++;
        }

        if (!item.uom || !['EA', 'KIT', 'SET', 'KG', 'L', 'M', 'CM', 'MM'].includes(item.uom)) {
          item.uom = 'EA';
          this._logFix('MBOM', item.name, 'Set UOM to EA');
          applied++;
        }

        if (!item.makeBuy || !['MAKE', 'BUY', 'RAW', 'CONSUMABLE'].includes(item.makeBuy)) {
          const ebomItem = ebomMap.get(item.findNumber);
          if (ebomItem && ebomItem.makeBuy) {
            item.makeBuy = ebomItem.makeBuy;
            this._logFix('MBOM', item.name, `Set makeBuy to match EBOM: ${item.makeBuy}`);
          } else {
            item.makeBuy = 'BUY';
            this._logFix('MBOM', item.name, 'Set makeBuy to BUY');
          }
          applied++;
        }

        if (item.makeBuy === 'MAKE' && !item.operationReference) {
          const opRef = String((index + 1) * 10).padStart(4, '0');
          item.operationReference = opRef;
          this._logFix('MBOM', item.name, `Added operation reference: ${opRef}`);
          applied++;
        }

        if (!item.ebomRef && item.findNumber) {
          const ebomItem = ebomMap.get(item.findNumber);
          if (ebomItem) {
            item.ebomRef = ebomItem.findNumber;
            this._logFix('MBOM', item.name, `Linked to EBOM: ${item.ebomRef}`);
            applied++;
          }
        }

        if (!item.description) {
          item.description = `${item.name} - Manufacturing Component`;
          this._logFix('MBOM', item.name, 'Added default description');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // BOP FIXES
  // ============================================
  _fixBOP(bopData, equipment) {
    let applied = 0;
    let failed = 0;
    const equipMap = new Map(equipment.map(e => [e.name, e]));

    const operations = this._extractOperations(bopData);
    const equipNames = Array.from(equipMap.keys());

    operations.forEach((op, index) => {
      try {
        if (!op.sequence || !/^[0-9]{4,6}$/.test(op.sequence)) {
          op.sequence = String((index + 1) * 10).padStart(4, '0');
          this._logFix('BOP', op.name || `Operation-${index}`, `Set sequence to ${op.sequence}`);
          applied++;
        }

        if (!op.name) {
          op.name = `Operation-${String(index + 1).padStart(3, '0')}`;
          this._logFix('BOP', `Operation-${index}`, `Set name to ${op.name}`);
          applied++;
        }

        if (!op.workCenter && !op.wc) {
          const defaultWC = equipNames.length > 0 ? equipNames[0] : 'WC-DEFAULT-001';
          op.workCenter = defaultWC;
          op.wc = defaultWC;
          this._logFix('BOP', op.name, `Set workCenter to ${defaultWC}`);
          applied++;
        } else if (!op.workCenter && op.wc) {
          op.workCenter = op.wc;
          this._logFix('BOP', op.name, 'Copied wc to workCenter');
          applied++;
        } else if (op.workCenter && !op.wc) {
          op.wc = op.workCenter;
          this._logFix('BOP', op.name, 'Copied workCenter to wc');
          applied++;
        }

        const wc = op.workCenter || op.wc;
        if (wc && !equipMap.has(wc) && equipNames.length > 0) {
          const defaultWC = equipNames[0];
          op.workCenter = defaultWC;
          op.wc = defaultWC;
          this._logFix('BOP', op.name, `Updated workCenter to existing: ${defaultWC}`);
          applied++;
        }

        if (!op.description) {
          op.description = `${op.name} - Manufacturing Operation`;
          this._logFix('BOP', op.name, 'Added default description');
          applied++;
        }

        if (!op.standardTime || op.standardTime === 0) {
          op.standardTime = 10;
          op.stdTime = 10;
          this._logFix('BOP', op.name, 'Set standardTime to 10 minutes');
          applied++;
        }

        if (!op.setupTime || op.setupTime === 0) {
          op.setupTime = 5;
          op.setup = 5;
          this._logFix('BOP', op.name, 'Set setupTime to 5 minutes');
          applied++;
        }

        if (!op.operationType) {
          op.operationType = 'General';
          this._logFix('BOP', op.name, 'Set operationType to General');
          applied++;
        }

        if (op.operationType === 'Machining' && !op.parameter) {
          op.parameter = 'Speed: 1000 RPM, Feed: 0.2 mm/rev, Depth: 0.5 mm';
          this._logFix('BOP', op.name, 'Added default machining parameters');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // EQUIPMENT (BOE) FIXES
  // ============================================
  _fixEquipment(equip) {
    let applied = 0;
    let failed = 0;

    equip.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `EQ-${index + 1}-${Date.now()}`;
          this._logFix('BOE', item.name || `Equipment-${index}`, 'Added ID');
          applied++;
        }

        if (!item.name) {
          item.name = `Equipment-${String(index + 1).padStart(3, '0')}`;
          this._logFix('BOE', `Equipment-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.level || !['Enterprise', 'Site', 'Area', 'ProductionLine', 'ProcessCell', 'WorkUnit', 'StorageZone'].includes(item.level)) {
          item.level = 'WorkUnit';
          this._logFix('BOE', item.name, 'Set level to WorkUnit');
          applied++;
        }

        if (!item.technology || !['Conventional', 'Casting', 'CNC', '3D Printing', 'Robotics'].includes(item.technology)) {
          item.technology = 'Conventional';
          this._logFix('BOE', item.name, 'Set technology to Conventional');
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - ${item.level} equipment`;
          this._logFix('BOE', item.name, 'Added default description');
          applied++;
        }

        if (!item.costRate || item.costRate === 0) {
          item.costRate = 100;
          this._logFix('BOE', item.name, 'Set costRate to $100/h');
          applied++;
        }

        if (!item.capacity || item.capacity === 0) {
          item.capacity = 720;
          this._logFix('BOE', item.name, 'Set capacity to 720 hours');
          applied++;
        }

        if (!item.bmideType) {
          item.bmideType = 'MESWorkUnit';
          this._logFix('BOE', item.name, 'Set bmideType to MESWorkUnit');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // TOOLS FIXES
  // ============================================
  _fixTools(tools) {
    let applied = 0;
    let failed = 0;

    tools.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `TOOL-${index + 1}-${Date.now()}`;
          this._logFix('Tools', item.name || `Tool-${index}`, 'Added ID');
          applied++;
        }

        if (!item.name) {
          item.name = `Tool-${String(index + 1).padStart(3, '0')}`;
          this._logFix('Tools', `Tool-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.type || !['Fixture', 'Cutting Tool', 'Measuring Device', 'Tooling', 'Consumable'].includes(item.type)) {
          item.type = 'Fixture';
          this._logFix('Tools', item.name, 'Set type to Fixture');
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - ${item.type}`;
          this._logFix('Tools', item.name, 'Added default description');
          applied++;
        }

        if (!item.quantity || item.quantity <= 0) {
          item.quantity = 1;
          this._logFix('Tools', item.name, 'Set quantity to 1');
          applied++;
        }

        if (!item.calibrationDue) {
          const date = new Date();
          date.setFullYear(date.getFullYear() + 1);
          item.calibrationDue = date.toISOString().split('T')[0];
          this._logFix('Tools', item.name, 'Set calibration due date to 1 year from now');
          applied++;
        }

        if (!item.manufacturer) {
          item.manufacturer = 'Standard Manufacturer';
          this._logFix('Tools', item.name, 'Set default manufacturer');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // SKILLS FIXES
  // ============================================
  _fixSkills(skills) {
    let applied = 0;
    let failed = 0;

    skills.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `SKILL-${index + 1}-${Date.now()}`;
          this._logFix('Skills', item.name || `Skill-${index}`, 'Added ID');
          applied++;
        }

        if (!item.name) {
          item.name = `Skill-${String(index + 1).padStart(3, '0')}`;
          this._logFix('Skills', `Skill-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.level || item.level < 1 || item.level > 5) {
          item.level = 1;
          this._logFix('Skills', item.name, 'Set level to 1');
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - Level ${item.level} skill`;
          this._logFix('Skills', item.name, 'Added default description');
          applied++;
        }

        if (!item.validityMonths || item.validityMonths <= 0) {
          item.validityMonths = 24;
          this._logFix('Skills', item.name, 'Set validityMonths to 24');
          applied++;
        }

        if (!item.certificationBody) {
          item.certificationBody = 'ISO Standard';
          this._logFix('Skills', item.name, 'Set default certification body');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // QUALITY PLANS FIXES
  // ============================================
  _fixQualityPlans(qualityPlans) {
    let applied = 0;
    let failed = 0;

    qualityPlans.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `QP-${index + 1}-${Date.now()}`;
          this._logFix('Quality', item.name || `QualityPlan-${index}`, 'Added ID');
          applied++;
        }

        if (!item.name) {
          item.name = `QualityPlan-${String(index + 1).padStart(3, '0')}`;
          this._logFix('Quality', `QualityPlan-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - Quality Plan`;
          this._logFix('Quality', item.name, 'Added default description');
          applied++;
        }

        if (!item.inspectionFrequency) {
          item.inspectionFrequency = 'Every 100 pieces';
          this._logFix('Quality', item.name, 'Set inspection frequency to Every 100 pieces');
          applied++;
        }

        if (!item.characteristics || item.characteristics.length === 0) {
          item.characteristics = [
            {
              name: 'Dimension Check',
              description: 'Verify critical dimensions',
              tolerance: '±0.1mm',
              criticality: 'Critical',
              inspectionMethod: 'CMM'
            },
            {
              name: 'Surface Finish',
              description: 'Check surface quality',
              tolerance: 'Ra 1.6',
              criticality: 'Standard',
              inspectionMethod: 'Visual'
            }
          ];
          this._logFix('Quality', item.name, 'Added default characteristics');
          applied++;
        }

        (item.characteristics || []).forEach((qc, qci) => {
          if (!qc.name) {
            qc.name = `Characteristic-${qci + 1}`;
            this._logFix('Quality', item.name, 'Added default characteristic name');
            applied++;
          }

          if (!qc.tolerance) {
            qc.tolerance = '±0.1mm';
            this._logFix('Quality', item.name, 'Set default tolerance');
            applied++;
          }

          if (!qc.inspectionMethod) {
            qc.inspectionMethod = 'Manual';
            this._logFix('Quality', item.name, 'Set default inspection method');
            applied++;
          }
        });
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // EWI FIXES
  // ============================================
  _fixEWI(ewiDocs) {
    let applied = 0;
    let failed = 0;

    ewiDocs.forEach((item, index) => {
      try {
        if (!item.id) {
          item.id = `EWI-${index + 1}-${Date.now()}`;
          this._logFix('EWI', item.name || `EWI-${index}`, 'Added ID');
          applied++;
        }

        if (!item.name) {
          item.name = `EWI-${String(index + 1).padStart(3, '0')}`;
          this._logFix('EWI', `EWI-${index}`, `Set name to ${item.name}`);
          applied++;
        }

        if (!item.type || !['PDF', 'WORD', 'PPT', 'VIDEO', '3D'].includes(item.type)) {
          item.type = 'PDF';
          this._logFix('EWI', item.name, 'Set type to PDF');
          applied++;
        }

        if (!item.description) {
          item.description = `${item.name} - Work Instruction`;
          this._logFix('EWI', item.name, 'Added default description');
          applied++;
        }

        if (!item.revision) {
          item.revision = 'A';
          this._logFix('EWI', item.name, 'Set revision to A');
          applied++;
        }

        if (!item.author) {
          item.author = 'System Admin';
          this._logFix('EWI', item.name, 'Set default author');
          applied++;
        }

        if (!item.approvalDate) {
          const date = new Date();
          item.approvalDate = date.toISOString().split('T')[0];
          this._logFix('EWI', item.name, 'Set approval date to today');
          applied++;
        }
      } catch (error) {
        failed++;
      }
    });

    return { applied, failed };
  }

  // ============================================
  // LOG FIX
  // ============================================
  _logFix(domain, item, message) {
    this.fixHistory.push({
      domain,
      item,
      message,
      timestamp: new Date().toISOString()
    });
  }
}

// ============================================
// 4. GUARDRAIL UI COMPONENT WITH REPORT
// ============================================

export const DigitalEngineeringGuardrailDashboard = ({ artifacts, onFix, onUpdate }) => {
  const [engine, setEngine] = useState(() => new DigitalEngineeringGuardrailEngine({
    autoFix: false,
    productType: artifacts?.product?.productType || 'mechanical',
    industry: artifacts?.product?.industry || 'General'
  }));
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('issues');
  const [isFixing, setIsFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const runValidation = useCallback(() => {
    const result = engine.validate(artifacts || {});
    setReport(result);
    setFixResult(null);
  }, [artifacts, engine]);

  const toggleExpand = useCallback((key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const getRecommendations = useCallback(() => {
    if (!report) return [];
    return engine.getRecommendations();
  }, [report, engine]);

  const handleAutoFixAll = useCallback(async () => {
    setIsFixing(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    const result = engine.autoFixAll(artifacts);
    setFixResult(result);
    if (onUpdate && result.artifacts) {
      onUpdate(result.artifacts);
    }
    const newReport = engine.validate(result.artifacts);
    setReport(newReport);
    setIsFixing(false);
  }, [artifacts, engine, onUpdate]);

  const downloadReport = useCallback(() => {
    if (!fixResult || !fixResult.report) return;
    
    const reportData = {
      ...fixResult.report,
      artifacts: fixResult.artifacts,
      fixHistory: fixResult.fixHistory
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `GuardRails_Report_${new Date().toISOString().slice(0,19).replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [fixResult]);

  useEffect(() => {
    runValidation();
  }, [runValidation]);

  if (!report) {
    return <div style={{ padding: '20px', color: T.t2 }}>Running Digital Engineering GuardRail validation...</div>;
  }

  const filteredIssues = report.issues.filter(issue => {
    if (filter === 'all') return true;
    return issue.level === filter;
  });

  const score = engine.getProductReadinessScore();
  const recommendations = getRecommendations();
  const hasIssues = report.issues.length > 0;

  return (
    <div style={{ background: T.s2, borderRadius: '8px', padding: '12px', border: `1px solid ${T.b}` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Shield size={18} color={score >= 80 ? T.g : score >= 60 ? T.w : T.d} />
          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: T.pl }}>Digital Engineering GuardRails</span>
          <span style={{ ...S.badge(score >= 80 ? T.g : score >= 60 ? T.w : T.d), fontSize: '0.7rem' }}>
            {Math.round(score)}% Readiness
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={runValidation} style={{ ...S.btn, ...S.bPri, padding: '2px 8px', fontSize: '0.6rem' }}>
            <RefreshCw size={10} /> Revalidate
          </button>
          <button onClick={() => setViewMode(m => m === 'issues' ? 'recommendations' : 'issues')} 
            style={{ ...S.btn, ...S.bTeal, padding: '2px 8px', fontSize: '0.6rem' }}>
            {viewMode === 'issues' ? '💡 Recommendations' : '📋 Issues'}
          </button>
          <button onClick={handleAutoFixAll} disabled={isFixing || !hasIssues} 
            style={{ ...S.btn, background: !hasIssues ? T.s : (isFixing ? T.w : T.g), color: !hasIssues ? T.t3 : 'white', 
            padding: '2px 10px', fontSize: '0.6rem', opacity: (!hasIssues || isFixing) ? 0.6 : 1,
            cursor: (!hasIssues || isFixing) ? 'not-allowed' : 'pointer',
            border: `1px solid ${!hasIssues ? T.b : (isFixing ? T.w : T.g)}` }}
            title={!hasIssues ? "No issues to fix" : (isFixing ? "Fixing in progress..." : "Auto-fix all issues")}>
            {isFixing ? <><RefreshCw size={10} className="spin" /> Fixing...</> : <><Wrench size={10} /> Auto-Fix All {hasIssues ? `(${report.issues.length})` : '✅'}</>}
          </button>
        </div>
      </div>

      {/* Fix Result Summary */}
      {fixResult && (
        <div style={{ background: fixResult.fixesApplied > 0 ? `${T.g}15` : `${T.w}15`, border: `1px solid ${fixResult.fixesApplied > 0 ? T.g : T.w}`,
          borderRadius: '4px', padding: '8px 12px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {fixResult.fixesApplied > 0 ? <CheckCircle size={16} color={T.g} /> : <AlertCircle size={16} color={T.w} />}
              <span style={{ fontSize: '0.7rem', color: T.t }}>
                {fixResult.report?.summary?.statusMessage || 
                  (fixResult.fixesApplied > 0 ? `✅ Fixed ${fixResult.fixesApplied} issues` : 'ℹ️ No issues to fix')}
                {fixResult.fixesFailed > 0 && ` (${fixResult.fixesFailed} failed)`}
                {fixResult.remainingIssues > 0 && ` | ${fixResult.remainingIssues} remaining`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {fixResult.report && (
                <>
                  <button onClick={() => setShowReportModal(true)} style={{ ...S.btn, ...S.bPP, padding: '2px 8px', fontSize: '0.55rem' }}>
                    <FileText size={10} /> View Report
                  </button>
                  <button onClick={downloadReport} style={{ ...S.btn, ...S.bTeal, padding: '2px 8px', fontSize: '0.55rem' }}>
                    <Download size={10} /> Download Report
                  </button>
                </>
              )}
              <button onClick={() => setFixResult(null)} style={{ background: 'none', border: 'none', color: T.t3, cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4px', marginBottom: '10px' }}>
        <div style={S.statCard}><div style={{ ...S.statVal, color: report.complianceMetrics.overall >= 80 ? T.g : T.w }}>
          {Math.round(report.complianceMetrics.overall)}%</div><div style={S.statLbl}>Overall</div></div>
        <div style={S.statCard}><div style={{ ...S.statVal, color: report.complianceMetrics.engineering >= 80 ? T.g : T.w }}>
          {Math.round(report.complianceMetrics.engineering)}%</div><div style={S.statLbl}>Engineering</div></div>
        <div style={S.statCard}><div style={{ ...S.statVal, color: report.complianceMetrics.manufacturing >= 80 ? T.g : T.w }}>
          {Math.round(report.complianceMetrics.manufacturing)}%</div><div style={S.statLbl}>Manufacturing</div></div>
        <div style={S.statCard}><div style={{ ...S.statVal, color: report.complianceMetrics.quality >= 80 ? T.g : T.w }}>
          {Math.round(report.complianceMetrics.quality)}%</div><div style={S.statLbl}>Quality</div></div>
      </div>

      {/* Issues Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '4px', marginBottom: '10px' }}>
        <div style={S.statCard}><div style={S.statVal}>{report.summary.total}</div><div style={S.statLbl}>Total Issues</div></div>
        <div style={{ ...S.statCard, borderColor: T.d }}><div style={{ ...S.statVal, color: T.d }}>{report.summary.critical}</div><div style={S.statLbl}>Critical</div></div>
        <div style={{ ...S.statCard, borderColor: T.w }}><div style={{ ...S.statVal, color: T.w }}>{report.summary.warnings}</div><div style={S.statLbl}>Warnings</div></div>
        <div style={{ ...S.statCard, borderColor: T.cy }}><div style={{ ...S.statVal, color: T.cy }}>{report.summary.info}</div><div style={S.statLbl}>Info</div></div>
        <div style={{ ...S.statCard, borderColor: T.pp }}><div style={{ ...S.statVal, color: T.pp }}>{report.summary.suggestions}</div><div style={S.statLbl}>Suggestions</div></div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {['all', 'critical', 'warning', 'info', 'suggestion'].map((level) => (
          <button key={level} onClick={() => setFilter(level)} style={{ ...S.btn, 
            background: filter === level ? T.p : T.s, color: filter === level ? 'white' : T.t2,
            padding: '2px 8px', fontSize: '0.6rem' }}>
            {level.charAt(0).toUpperCase() + level.slice(1)} ({level === 'all' ? report.issues.length : report.summary[level] || 0})
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ maxHeight: '300px', overflowY: 'auto', background: T.bg, borderRadius: '4px', padding: '4px' }}>
        {viewMode === 'issues' ? (
          filteredIssues.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: T.t3, fontSize: '0.7rem' }}>✅ No issues found in this category</div>
          ) : (
            filteredIssues.map((issue, index) => {
              const key = `${issue.domain}-${issue.item}-${index}`;
              const isExpanded = expanded[key];
              const isFixed = fixResult?.fixHistory?.some(f => f.domain === issue.domain && f.item === issue.item);
              return (
                <div key={key} style={{ background: isFixed ? `${T.g}10` : (index % 2 === 0 ? T.s : 'transparent'),
                  padding: '4px 6px', borderRadius: '2px', cursor: 'pointer',
                  borderLeft: `2px solid ${isFixed ? T.g : issue.level === RULE_CATEGORIES.CRITICAL ? T.d : issue.level === RULE_CATEGORIES.WARNING ? T.w : issue.level === RULE_CATEGORIES.INFO ? T.cy : T.pp}` }}
                  onClick={() => toggleExpand(key)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.65rem' }}>
                      {isFixed && <CheckCircle size={10} color={T.g} />}
                      <span style={{ ...S.badge(isFixed ? T.g : issue.level === RULE_CATEGORIES.CRITICAL ? T.d : issue.level === RULE_CATEGORIES.WARNING ? T.w : issue.level === RULE_CATEGORIES.INFO ? T.cy : T.pp), fontSize: '0.5rem' }}>
                        {isFixed ? 'Fixed' : issue.level}
                      </span>
                      <span style={{ color: T.t2 }}>[{issue.domain}]</span>
                      <span style={{ color: T.t }}>{issue.item}</span>
                      <span style={{ color: T.t3 }}>: {issue.message}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {issue.fix && <span style={{ fontSize: '0.5rem', color: T.te }}>💡 Fix available</span>}
                      <span style={{ fontSize: '0.5rem', color: T.t3 }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: '4px 8px', fontSize: '0.6rem', color: T.t2, background: `${T.b}50`, borderRadius: '2px', marginTop: '2px' }}>
                      {issue.fix && <div>🔧 Fix: {issue.fix}</div>}
                      {issue.field && <div>📁 Field: {issue.field}</div>}
                      {isFixed && <div style={{ color: T.g }}>✅ This issue was automatically fixed</div>}
                    </div>
                  )}
                </div>
              );
            })
          )
        ) : (
          recommendations.map((rec, recIndex) => (
            <div key={recIndex} style={{ background: T.s, padding: '6px 8px', borderRadius: '4px', marginBottom: '4px',
              borderLeft: `2px solid ${rec.priority === 'High' ? T.d : rec.priority === 'Medium' ? T.w : T.cy}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 600, color: T.pl }}>{rec.category}</span>
                <span style={{ fontSize: '0.5rem', ...S.badge(rec.priority === 'High' ? T.d : rec.priority === 'Medium' ? T.w : T.cy) }}>{rec.priority}</span>
              </div>
              {rec.items.map((item, itemIndex) => (
                <div key={itemIndex} style={{ fontSize: '0.6rem', color: T.t2, padding: '2px 4px', marginLeft: '4px',
                  borderBottom: itemIndex < rec.items.length - 1 ? `1px solid ${T.b}` : 'none' }}>
                  <div>{item.issue}</div>
                  <div style={{ color: T.te, fontSize: '0.55rem', paddingLeft: '8px' }}>💡 {item.fix}</div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Domain Breakdown */}
      <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '4px' }}>
        {Object.entries(report.byDomain).map(([domain, issues]) => {
          const fixedCount = fixResult?.fixHistory?.filter(f => f.domain === domain).length || 0;
          const remaining = issues.length - fixedCount;
          return (
            <div key={domain} style={{ background: T.s, padding: '4px 6px', borderRadius: '4px', textAlign: 'center', border: `1px solid ${T.b}` }}>
              <div style={{ fontSize: '0.6rem', color: T.t2 }}>{domain}</div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: remaining > 0 ? (issues.filter(i => i.level === RULE_CATEGORIES.CRITICAL).length > 0 ? T.d : T.w) : T.g }}>
                {remaining > 0 ? remaining : '✅'}{fixedCount > 0 && ` (${fixedCount} fixed)`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Digital Thread Status */}
      <div style={{ marginTop: '8px', padding: '6px 8px', background: T.s, borderRadius: '4px', border: `1px solid ${T.b}`,
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.6rem', color: T.t2, fontWeight: 600 }}>Digital Thread Status:</span>
        {['Product', 'EBOM', 'MBOM', 'BOP', 'BOE', 'Quality', 'EWI'].map((artifact) => {
          const artifactKey = artifact.toLowerCase().replace(' ', '');
          const hasArtifact = artifacts[artifactKey] || (artifact === 'BOP' && artifacts.bopData) ||
            (artifact === 'BOE' && artifacts.equip) || (artifact === 'Quality' && artifacts.qualityPlans) ||
            (artifact === 'EWI' && artifacts.ewiDocs);
          const hasIssues = report.issues.some(i => i.domain?.includes(artifact));
          const isFixed = fixResult?.fixHistory?.some(f => f.domain?.includes(artifact));
          return (
            <div key={artifact} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 6px', borderRadius: '3px',
              background: hasArtifact ? (hasIssues && !isFixed ? `${T.w}22` : `${T.g}22`) : `${T.d}22`,
              border: `1px solid ${hasArtifact ? (hasIssues && !isFixed ? T.w : T.g) : T.d}` }}>
              <span style={{ fontSize: '0.6rem', color: hasArtifact ? (hasIssues && !isFixed ? T.w : T.g) : T.d }}>
                {hasArtifact ? (hasIssues && !isFixed ? '⚠️' : '✅') : '❌'}
              </span>
              <span style={{ fontSize: '0.55rem', color: T.t2 }}>{artifact}</span>
              {isFixed && <CheckCircle size={8} color={T.g} />}
            </div>
          );
        })}
      </div>

      {/* Report Modal */}
      {showReportModal && fixResult?.report && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: T.s2, borderRadius: '10px', padding: '16px', width: '90vw', maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.b}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontWeight: 700, fontSize: '1rem', color: T.pl, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FileText size={18} /> Fix Report
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={downloadReport} style={{ ...S.btn, ...S.bSuc, padding: '3px 8px', fontSize: '0.65rem' }}>
                  <Download size={12} /> Download
                </button>
                <button onClick={() => setShowReportModal(false)} style={{ ...S.btn, ...S.bGho, padding: '3px 8px', fontSize: '0.65rem' }}>
                  <X size={12} /> Close
                </button>
              </div>
            </div>

            {/* Report Content */}
            <div style={{ background: T.bg, borderRadius: '4px', padding: '12px', border: `1px solid ${T.b}` }}>
              {/* Summary */}
              <div style={{ marginBottom: '12px' }}>
                <h3 style={{ color: T.pl, fontSize: '0.85rem', marginBottom: '6px' }}>📊 Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                  <div style={S.statCard}>
                    <div style={{ ...S.statVal, color: fixResult.report.summary.overallStatus === 'success' ? T.g : T.w }}>
                      {fixResult.report.summary.overallStatus}
                    </div>
                    <div style={S.statLbl}>Status</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statVal}>{fixResult.report.summary.fixesApplied}</div>
                    <div style={S.statLbl}>Fixed</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={{ ...S.statVal, color: T.d }}>{fixResult.report.summary.fixesFailed}</div>
                    <div style={S.statLbl}>Failed</div>
                  </div>
                  <div style={S.statCard}>
                    <div style={S.statVal}>{fixResult.report.summary.issuesRemaining}</div>
                    <div style={S.statLbl}>Remaining</div>
                  </div>
                </div>
                <div style={{ marginTop: '6px', fontSize: '0.65rem', color: T.t2, textAlign: 'center' }}>
                  {fixResult.report.summary.statusMessage} | Time: {fixResult.report.summary.totalTime}s | Success Rate: {fixResult.report.summary.successRate}%
                </div>
              </div>

              {/* Created Artifacts */}
              {fixResult.report.details.createdArtifacts.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ color: T.g, fontSize: '0.8rem', marginBottom: '4px' }}>✅ Created Artifacts</h3>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {fixResult.report.details.createdArtifacts.map((item, i) => (
                      <span key={i} style={{ ...S.badge(T.g), fontSize: '0.6rem', padding: '2px 8px' }}>
                        {item.domain}: {item.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Fixes by Domain */}
              {Object.keys(fixResult.report.details.fixesByDomain).length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ color: T.cy, fontSize: '0.8rem', marginBottom: '4px' }}>🔧 Fixes by Domain</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '4px' }}>
                    {Object.entries(fixResult.report.details.fixesByDomain).map(([domain, fixes]) => (
                      <div key={domain} style={{ background: T.s, padding: '4px 8px', borderRadius: '4px', border: `1px solid ${T.b}` }}>
                        <span style={{ fontWeight: 600, fontSize: '0.65rem', color: T.pl }}>{domain}</span>
                        <span style={{ fontSize: '0.55rem', color: T.t3, marginLeft: '4px' }}>{fixes.length} fixes</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Failed Fixes */}
              {fixResult.report.details.failedFixes.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <h3 style={{ color: T.d, fontSize: '0.8rem', marginBottom: '4px' }}>❌ Failed Fixes</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {fixResult.report.details.failedFixes.map((fix, i) => (
                      <div key={i} style={{ background: `${T.d}10`, padding: '4px 8px', borderRadius: '4px', border: `1px solid ${T.d}30`, fontSize: '0.6rem', color: T.t2 }}>
                        <span style={{ color: T.d }}>✗</span> {fix.domain}: {fix.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Initial vs Final State */}
              <div>
                <h3 style={{ color: T.pp, fontSize: '0.8rem', marginBottom: '4px' }}>📋 Digital Thread State</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div style={{ background: T.s, padding: '6px 8px', borderRadius: '4px', border: `1px solid ${T.b}` }}>
                    <div style={{ fontSize: '0.55rem', color: T.t3, marginBottom: '2px' }}>Before Fix</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {Object.entries(fixResult.report.details.initialState).map(([key, value]) => (
                        <span key={key} style={{ fontSize: '0.5rem', color: value ? T.g : T.d }}>
                          {key}: {value ? '✅' : '❌'}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: T.s, padding: '6px 8px', borderRadius: '4px', border: `1px solid ${T.b}` }}>
                    <div style={{ fontSize: '0.55rem', color: T.t3, marginBottom: '2px' }}>After Fix</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {Object.entries(fixResult.report.details.finalState).map(([key, value]) => (
                        <span key={key} style={{ fontSize: '0.5rem', color: value ? T.g : T.d }}>
                          {key}: {value ? '✅' : '❌'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

// ============================================
// 5. EXPORTS
// ============================================

export { DigitalEngineeringGuardrailEngine as GuardrailEngine };
export { DigitalEngineeringGuardrailDashboard as GuardrailDashboard };

export default {
  DigitalEngineeringGuardrailEngine,
  DigitalEngineeringGuardrailDashboard,
  GuardrailEngine: DigitalEngineeringGuardrailEngine,
  GuardrailDashboard: DigitalEngineeringGuardrailDashboard,
  RULE_CATEGORIES,
  PRODUCT_TYPES,
  INDUSTRY_STANDARDS
};